import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  title: string;
  message: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmModal = ({ title, message, busy, onConfirm, onCancel }: Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onCancel();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, busy]);

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}
    >
      <div
        role='alertdialog'
        aria-modal='true'
        aria-label={title}
        className='flex w-full max-w-md flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-xl'
      >
        <h2 className='text-base font-semibold text-foreground'>{title}</h2>
        <p className='text-sm text-muted-foreground'>{message}</p>
        <div className='flex justify-end gap-2'>
          <button
            type='button'
            onClick={onCancel}
            disabled={busy}
            className='rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50'
          >
            {t('pages.setup.cancel')}
          </button>
          <button
            type='button'
            onClick={onConfirm}
            disabled={busy}
            className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50'
          >
            {busy ? t('pages.setup.working') : t('pages.setup.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
